import { FormSection as Section } from "$components/form-section";
import { SelectField, TextField } from "$components/rhf-fields";
import type { GetLinkedGuardianDetailsQuery } from "$routes/edit/patient/_lib/__generated__/v2-editable-patient-details.gql";
import { GuardianRelationship } from "elephant-web/gql";
import React from "react";
import { useWatch } from "react-hook-form";
import { defineMessage, useIntl } from "react-intl";
import { LinkedGuardianDetails } from "./linked-guardian-details";
import {
  PhoneOwnershipRadioInput,
  PhoneOwnershipRadioOptions,
} from "./phone-ownership";

const RHFHideIf: FCwC<{
  name: string;
  condition: (value: unknown) => boolean;
}> = ({ name, condition, children }) => {
  const valueOfField = useWatch({ name });
  return condition(valueOfField) ? null : <>{children}</>;
};

const PhoneNumberInput: FC<React.ComponentProps<typeof TextField>> = (
  delegateProps,
) => {
  const { formatMessage } = useIntl();
  return (
    <TextField
      id="phone-number"
      type="tel"
      label={formatMessage({
        id: "patient.contactSection.phoneNumber.label",
        defaultMessage: "Phone Number",
      })}
      data-testid="phoneNumber"
      {...delegateProps}
    />
  );
};

export const GuardianSection: FC<{
  hasLinkedGuardian: boolean;
  linkedGuardian?: GetLinkedGuardianDetailsQuery["patient"] & {
    relationship: string;
  };
  isLinkedGuardianLoading?: boolean;
  errorLoadingDetails?: boolean;
  onClickUnlink?: () => void;
  fieldNames: {
    fullName: string;
    phoneNumber: string;
    phoneOwnership: string;
    relationship: string;
  };
}> = ({
  hasLinkedGuardian,
  linkedGuardian,
  isLinkedGuardianLoading,
  onClickUnlink,
  errorLoadingDetails,
  fieldNames,
}) => {
  const { formatMessage } = useIntl();

  return (
    <Section
      title={formatMessage({
        id: "patient.guardianSection.heading",
        defaultMessage: "Guardian",
      })}
    >
      {hasLinkedGuardian ? (
        <LinkedGuardianDetails
          details={
            linkedGuardian
              ? {
                  patientId: linkedGuardian.id,
                  fullName: linkedGuardian.person.name.fullname,
                  phone: linkedGuardian.person.contactPoints.find(
                    (cp) => cp.system === "phone",
                  )?.value,
                  relationship: linkedGuardian.relationship,
                }
              : undefined
          }
          onClickUnlink={onClickUnlink}
          areDetailsLoading={isLinkedGuardianLoading}
          errorLoadingDetails={errorLoadingDetails}
        />
      ) : (
        <>
          <TextField
            name={fieldNames.fullName}
            label={formatMessage({
              id: "patient.guardianSection.fullName.label",
              defaultMessage: "Full Name",
            })}
            required
            fullWidth
            data-testid="fullName"
          />

          <PhoneOwnershipRadioInput
            fullWidth
            name={fieldNames.phoneOwnership}
          />
          <RHFHideIf
            name={fieldNames.phoneOwnership}
            condition={(value) =>
              !value || value === PhoneOwnershipRadioOptions.None
            }
          >
            <PhoneNumberInput
              required
              fullWidth
              name={fieldNames.phoneNumber}
            />
          </RHFHideIf>

          <SelectField
            fullWidth
            name={fieldNames.relationship}
            label={formatMessage({
              id: "patient.guardianSection.relationship.label",
              defaultMessage: "Relationship",
            })}
            data-testid="relationship"
            options={Object.values(GuardianRelationship).map((code) => ({
              label: formatMessage(guardianRelationshipMap[code]),
              value: code,
            }))}
          />
        </>
      )}
    </Section>
  );
};

export const guardianRelationshipMap = {
  [GuardianRelationship.Father]: defineMessage({
    id: "guardianRelationship.father",
    defaultMessage: "Father",
  }),
  [GuardianRelationship.Mother]: defineMessage({
    id: "guardianRelationship.mother",
    defaultMessage: "Mother",
  }),

  [GuardianRelationship.Grandparent]: defineMessage({
    id: "guardianRelationship.grandparent",
    defaultMessage: "Grandparent",
  }),

  [GuardianRelationship.Other]: defineMessage({
    id: "guardianRelationship.other",
    defaultMessage: "Other",
  }),
};
